import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { getAuthApiClient } from "@shared/apis/sidekick/api-factories";
import { AuthControllerLoginWithEmailAndPassword200Response } from "@shared/apis/sidekick/generated";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { appLogger } from "@shared/services/app-logger";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@shared/services/toast";
import { useOtherAppName } from "@shared/hooks/useAppName";
import { useOtherDomainGroupUrl } from "@shared/hooks/useOtherDomainGroupUrl";
import { isWrongBrandApiError } from "@shared/utils/is-wrong-brand-error";

type Props = {
  onSuccess: (data: AuthControllerLoginWithEmailAndPassword200Response) => void;
  onError?: (error: any) => void;
};

type Params = {
  email: string;
  password: string;
};

export const useLogin = (props: Props) => {
  const { onSuccess, onError } = props;
  const api = getAuthApiClient();
  const otherAppName = useOtherAppName();
  const otherDomainLoginUrl = useOtherDomainGroupUrl("/login");

  const mutation = useMutation({
    mutationFn: async (params: Params) => {
      const response = await api.authControllerLoginWithEmailAndPassword({
        loginWithEmailAndPasswordRequest: {
          email: params.email,
          password: params.password,
        },
      });

      onSuccess(response);
      if (response.authFlow === "LOGIN") {
        appEventEmitter.emit({ eventType: "sidekick-auth-login" });
        return;
      }
      if (response.authFlow === "CHALLENGE") {
        appEventEmitter.emit({
          eventType: "sidekick-auth-login-challenge",
          challengeName: response.challengeName,
        });
      }
      //TODO: move this logic to another place
      localStorage.removeItem("@sidekick:registration-start-pending");

      return response;
    },
    onError: async (error: Error) => {
      const apiError = await apiResponseErrorHandler(error);
      appLogger.error("Failed to login", { error, apiError });
      onError?.(apiError);
      if (apiError.status === 401) {
        toast.error("Incorrect email or password.");
        return;
      }
      if (apiError.status === 403) {
        if (isWrongBrandApiError(apiError.errors)) {
          toast.error(
            <>
              This account is on <strong>{otherAppName}</strong>.{" "}
              <a href={otherDomainLoginUrl}>Sign in there</a>
            </>,
          );
          return;
        }
        toast.error(
          "You don't have access to sign in here. This could be because your account isn't set up for this app, or has been disabled. Contact support if this continues.",
        );
        return;
      }
      toast.error(apiError.errors[0]);
    },
  });

  return mutation;
};
